import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useAuth } from '@/feature/auth/hooks/useAuth';

import { ROUTES } from '../../routes/routeConstants';

const Header = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const { logout, user } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  let pageTitle = 'Dashboard';
  if (currentPath === ROUTES.ORGANIZATIONS) {
    pageTitle = 'Organizations';
  } else if (currentPath.startsWith('/organizations/')) {
    pageTitle = 'Organization Details';
  } else if (currentPath === ROUTES.DEVICES) {
    pageTitle = 'Devices';
  } else if (currentPath === ROUTES.USERS) {
    pageTitle = 'Users';
  } else if (currentPath.startsWith('/users/')) {
    pageTitle = 'User Details';
  } else if (currentPath === ROUTES.TOKEN_USAGE) {
    pageTitle = 'Token Usage';
  } else if (currentPath === ROUTES.AUDIT_TRAIL) {
    pageTitle = 'Audit Trail';
  } else if (currentPath === ROUTES.HELP_DESK) {
    pageTitle = 'Help Desk';
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleLogout = () => {
    logout();
    setIsDropdownOpen(false);
  };

  const displayName =
    user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`
      : user?.email || 'System Admin';

  return (
    <header className='flex justify-between items-center px-6 py-4 bg-app-header border-app-header-border h-16 box-border shrink-0 shadow-sm'>
      <div className='flex items-center gap-4'>
        <h1 className='text-[28px] font-bold text-gray-900 m-0 leading-tight'>{pageTitle}</h1>
      </div>
      <div className='flex items-center'>
        <div className='relative' ref={dropdownRef}>
          <div
            className='flex flex-row flex-nowrap items-center justify-start gap-3 cursor-pointer py-2 px-3 rounded-md transition-colors hover:bg-gray-100 [&_svg]:block [&_svg]:shrink-0 [&_svg]:w-7 [&_svg]:h-7 [&_svg]:text-gray-600 [&_svg]:m-0 [&_svg]:p-0'
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            role='button'
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsDropdownOpen(!isDropdownOpen);
              }
            }}
            aria-label='User menu'
            aria-expanded={isDropdownOpen}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              className='lucide lucide-circle-user w-7 h-7'
              aria-hidden='true'
            >
              <circle cx='12' cy='12' r='10'></circle>
              <circle cx='12' cy='10' r='3'></circle>
              <path d='M7 20.662V19a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.662'></path>
            </svg>
            <span className='block whitespace-nowrap leading-normal m-0 p-0 flex-[0_0_auto] font-semibold text-gray-800'>
              {displayName}
            </span>
          </div>
          {isDropdownOpen && (
            <div className='absolute top-[calc(100%+8px)] right-0 bg-white border border-gray-200 rounded-lg shadow-lg min-w-40 z-1000 overflow-hidden py-1'>
              <button
                type='button'
                className='flex items-center gap-3 w-full py-3 px-4 bg-transparent border-none text-left cursor-pointer text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 active:bg-gray-100'
                onClick={handleLogout}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='18'
                  height='18'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='#2d7a5f'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  className='shrink-0'
                >
                  <path d='M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4' />
                  <polyline points='16 17 21 12 16 7' />
                  <line x1='21' x2='9' y1='12' y2='12' />
                </svg>
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
